/**
 * Channels-Sub-Tab für Product Content (C1+C2 — Content-Block-Basis).
 *
 * C1: Datenlayer auf Content-Blocks, Feldliste mit Zeichenzähler + Versions-Dropdown.
 * C2: Vererbung (Master + Channel-Blocks gemerged), Connector-Header, Chip-Abweichungs-
 * Counts, „Vererbte Felder zeigen"-Toggle. Side-by-Side („Vergleiche mit") = C2-B.
 */

import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Globe, Columns2 } from 'lucide-react'
import { Loading, Alert, AlertDescription, EmptyState } from '@/components/v2/ui'
import { ChannelPillsSelector } from '@/components/products/v2/shared/ChannelPillsSelector'
import { VersionHistoryOffcanvas } from '@/components/versioning'
import { apiClient } from '@/lib/api-client'
import { getProductChannelPackages } from '@/lib/api/channel-packages'
import { EID } from '@/lib/element-ids.registry'
import { ConnectorHeaderCard } from './channels/ConnectorHeaderCard'
import { ChannelFieldsList } from './channels/ChannelFieldsList'
import { ChannelBulletsPanel } from './channels/ChannelBulletsPanel'
import { CompareTargetBar } from './channels/CompareTargetBar'
import { ContentActivitySidebar } from './channels/ContentActivitySidebar'
import { useProductActivity } from './channels/useProductActivity'
import { useSideBySide } from './channels/useSideBySide'
import type { ActivityEntry } from './channels/activityFeed'
import { getBlockLanguages, readLangText } from './channels/contentBlockLang'
import { buildChannelPills } from './channels/channelScope'
import { useChannelContentBlocks } from './channels/useChannelContentBlocks'
import { mergeFields, overrideCount, withAlwaysShownFields, type MergedField } from './channels/contentInheritance'
import type { ContentBlock, BlockType } from '@/types/content-blocks'
import type { ChannelPackage } from '@/types/channel-packages'

const BLOCK_ORDER: BlockType[] = ['title', 'short_description', 'description', 'bullets', 'seo_meta']

interface Props {
  productId: string
}

export function ProductContentChannelsTab({ productId }: Props) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [selectedChannelId, setSelectedChannelId] = useState('product')
  const [selectedLang, setSelectedLang] = useState('')
  const [showInherited, setShowInherited] = useState(true)
  const [isSyncing, setIsSyncing] = useState(false)
  const [compareBlock, setCompareBlock] = useState<ContentBlock | null>(null)

  const packagesQuery = useQuery({
    queryKey: ['channelPackages', productId],
    queryFn: () => getProductChannelPackages(productId),
    enabled: !!productId,
  })
  const packages: ChannelPackage[] = packagesQuery.data?.channel_packages ?? []

  const { masterBlocks, channelBlocksByPkg, isLoading, isError } = useChannelContentBlocks(productId, packages)

  const isProductScope = selectedChannelId === 'product'
  const selectedPackage = packages.find((p) => p.id === selectedChannelId)
  const isJTLChannel = selectedPackage?.channel?.workspace_integrations?.integration_type === 'jtl'
  const channelBlocks = isProductScope ? [] : (channelBlocksByPkg[selectedChannelId] ?? [])

  const fields: MergedField[] = useMemo(() => {
    const base = isProductScope
      ? [...masterBlocks]
          .sort((a, b) => BLOCK_ORDER.indexOf(a.block_type) - BLOCK_ORDER.indexOf(b.block_type))
          .map((block) => ({ blockType: block.block_type, block, inherited: false }))
      : mergeFields(masterBlocks, channelBlocks)
    // Text-Standardfelder immer anzeigen (auch leer) → fehlende als Platzhalter.
    return withAlwaysShownFields(
      base,
      isProductScope ? 'product' : 'channel_package',
      isProductScope ? productId : selectedChannelId,
    )
  }, [isProductScope, masterBlocks, channelBlocks, productId, selectedChannelId])

  const visibleFields = fields.filter((f) => isProductScope || showInherited || !f.inherited)

  const languages = useMemo(() => {
    const set = new Set<string>()
    masterBlocks.forEach((b) => getBlockLanguages(b.current_version?.content).forEach((l) => set.add(l)))
    return set.size > 0 ? Array.from(set).sort() : ['de']
  }, [masterBlocks])

  useEffect(() => {
    if (!selectedLang && languages.length > 0) setSelectedLang(languages[0])
  }, [languages, selectedLang])

  const pills = buildChannelPills(packages, t('productContent.channels.master')).map((pill) =>
    pill.isMaster ? pill : { ...pill, badgeCount: overrideCount(channelBlocksByPkg[pill.id] ?? []) || undefined },
  )

  const masterName = t('productContent.channels.master')
  const { sideBySide, setSideBySide, compareTargetId, setCompareTargetId, compareTargets, resolveTarget } =
    useSideBySide({
      isProductScope,
      packages,
      selectedChannelId,
      masterBlocks,
      channelBlocksByPkg,
      selectedLang,
      masterName,
    })

  const masterTextByType = useMemo(() => {
    const m = new Map<BlockType, string>()
    masterBlocks.forEach((b) => m.set(b.block_type, readLangText(b.current_version?.content, selectedLang)))
    return m
  }, [masterBlocks, selectedLang])

  const activity = useProductActivity(masterBlocks, channelBlocksByPkg, packages, masterName)
  const [highlightBlockType, setHighlightBlockType] = useState<BlockType | null>(null)
  const handleShowActivity = (entry: ActivityEntry) => {
    setSelectedChannelId(entry.scopeKey ?? 'product')
    setHighlightBlockType(entry.blockType)
    setTimeout(() => setHighlightBlockType(null), 2500)
  }

  const handleSync = async () => {
    setIsSyncing(true)
    try {
      await apiClient.post(`/api/v1/products/${productId}/descriptions/sync`, {})
      toast.success(t('toast.success.jtlSyncSuccess'))
      queryClient.invalidateQueries({ queryKey: ['content-blocks'] })
    } catch (error) {
      toast.error(t('toast.error.jtlSync'), {
        description: error instanceof Error ? error.message : t('errors.generic'),
      })
    } finally {
      setIsSyncing(false)
    }
  }

  if (packagesQuery.isLoading) {
    return <div className="flex justify-center py-12"><Loading size="lg" label={t('common.loading')} /></div>
  }
  if (isError) {
    return <Alert variant="error"><AlertDescription>{t('productContent.channels.loadError')}</AlertDescription></Alert>
  }

  return (
    <div className="product-content-channels-layout flex h-full min-h-0 border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden">
      <div className="product-content-channels flex-1 min-h-0 flex flex-col">
      {/* Zeile 1: Channel-Pills (Q5: eigene Zeile) + Side-by-Side-Toggle */}
      <div className="flex items-center justify-between gap-3 px-3 pt-3 pb-2 bg-neutral-50 dark:bg-neutral-900/70 flex-wrap">
        <ChannelPillsSelector channels={pills} activeChannelId={selectedChannelId} onChange={setSelectedChannelId} />
        <button
          type="button"
          id={EID['content-sidebyside-toggle']}
          onClick={() => setSideBySide((v) => !v)}
          aria-pressed={sideBySide}
          className={`flex items-center gap-1.5 text-sm px-3 py-1 rounded border ${sideBySide ? 'bg-primary-500 text-white border-primary-500' : 'bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300'}`}
        >
          <Columns2 className="h-3.5 w-3.5" aria-hidden="true" />
          {t('productContent.channels.sideBySide')}
        </button>
      </div>

      {/* Zeile 2: Sprach-Selector (Q5: eigene Zeile unter den Pills) */}
      {languages.length > 1 && (
        <div className="flex items-center gap-2 px-3 pb-3 border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900/70">
          <label htmlFor={EID['content-channel-language-select']} className="text-xs text-neutral-500 dark:text-neutral-400">
            {t('productContent.channels.language')}
          </label>
          <select
            id={EID['content-channel-language-select']}
            value={selectedLang}
            onChange={(e) => setSelectedLang(e.target.value)}
            className="text-sm px-2 py-1 rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900"
            aria-label={t('productContent.channels.language')}
          >
            {languages.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
          </select>
        </div>
      )}

      {/* Connector-Header (nur bei Channel-Scope) */}
      {!isProductScope && selectedPackage && (
        <ConnectorHeaderCard
          channelName={selectedPackage.channel?.name ?? selectedChannelId}
          integrationType={selectedPackage.channel?.workspace_integrations?.integration_type}
          connectorVersion={selectedPackage.channel?.connector_version}
          overrideCount={overrideCount(channelBlocks)}
          showInherited={showInherited}
          onToggleInherited={setShowInherited}
          onSync={handleSync}
          isSyncing={isSyncing}
        />
      )}

      {/* „Vergleiche mit"-Zielleiste (nur im Side-by-Side-Modus) */}
      {sideBySide && compareTargets.length > 0 && (
        <CompareTargetBar targets={compareTargets} activeId={compareTargetId} onChange={setCompareTargetId} />
      )}

      <div className="flex-1 min-h-0 overflow-auto p-3 space-y-3">
        {isLoading && <div className="flex justify-center py-8"><Loading size="md" /></div>}
        {!isLoading && visibleFields.length === 0 && (
          <EmptyState icon={<Globe className="h-12 w-12" />} title={t('productContent.channels.noDescriptions')} />
        )}
        <ChannelFieldsList
          fields={visibleFields}
          lang={selectedLang}
          productId={productId}
          selectedChannelId={selectedChannelId}
          isProductScope={isProductScope}
          selectedPackage={selectedPackage}
          isJTLChannel={isJTLChannel}
          sideBySide={sideBySide && !!compareTargetId}
          resolveTarget={resolveTarget}
          highlightBlockType={highlightBlockType}
          masterValueFor={(bt) => masterTextByType.get(bt)}
          aiChannelId={isProductScope ? undefined : selectedPackage?.channel_id}
          onCompare={(block) => setCompareBlock(block)}
        />
        {!isProductScope && isJTLChannel && selectedPackage && (
          <ChannelBulletsPanel
            productId={productId}
            readChannelId={selectedPackage.channel_id}
            saveChannelId={selectedChannelId}
            lang={selectedLang}
          />
        )}
      </div>

      {compareBlock && (
        <VersionHistoryOffcanvas
          entityType="content_block"
          entityId={compareBlock.id}
          blockType={compareBlock.block_type}
          isOpen={!!compareBlock}
          onClose={() => setCompareBlock(null)}
        />
      )}
      </div>
      <ContentActivitySidebar entries={activity.entries} isLoading={activity.isLoading} onShow={handleShowActivity} />
    </div>
  )
}
